/**
 * Shared thehope.one brand tokens
 *
 * Visual language:
 * - Deep forest = trust / permanence / credibility
 * - Living green = action / hope / progress
 * - Sage = community / restoration
 * - Ivory = openness / humanity
 * - Muted blue = care / wellbeing
 */

/* -------------------------------------------------------------------------- */
/* Core brand colors                                                          */
/* -------------------------------------------------------------------------- */

export const HOPE_FOREST =
  "light-dark(#111111, #FFFFFF)";

export const HOPE_FOREST_DEEP =
  "light-dark(#111111, #FFFFFF)";

export const HOPE_GREEN =
  "light-dark(#111111, #FFFFFF)";

export const HOPE_GREEN_LIGHT =
  "light-dark(#2A2A2A, #E8E8E8)";

export const HOPE_GREEN_DIM =
  "light-dark(#111111, #FFFFFF)";

export const HOPE_GREEN_LABEL =
  "light-dark(#575757, #B0B0B0)";

/* -------------------------------------------------------------------------- */
/* Supporting colors                                                          */
/* -------------------------------------------------------------------------- */

export const HOPE_SAGE =
  "light-dark(#F4F4F4, #1A1A1A)";

export const HOPE_SAGE_SOFT =
  "light-dark(#F7F7F7, #141414)";

export const HOPE_BLUE =
  "light-dark(#7897A5, #9CB5C0)";

export const HOPE_BLUE_SOFT =
  "light-dark(#DDE9ED, #243940)";

export const HOPE_SAND =
  "light-dark(#D7C6A5, #8F8065)";

export const HOPE_SAND_SOFT =
  "light-dark(#EFE7D7, #332E25)";

/* -------------------------------------------------------------------------- */
/* Surface colors                                                             */
/* -------------------------------------------------------------------------- */

export const HOPE_CARD_SURFACE =
  "light-dark(#FFFFFF, #12251F)";

export const HOPE_PAGE_SURFACE =
  "light-dark(#FFFFFF, #000000)";

export const HOPE_PAGE_SURFACE_SOFT =
  "light-dark(#FFFFFF, #000000)";

/* -------------------------------------------------------------------------- */
/* Text colors                                                                */
/* -------------------------------------------------------------------------- */

export const HOPE_TEXT =
  "light-dark(#111111, #FFFFFF)";

export const HOPE_TEXT_MUTED =
  "light-dark(#575757, #B0B0B0)";

export const HOPE_TEXT_SOFT =
  "light-dark(#757575, #929292)";

/* -------------------------------------------------------------------------- */
/* Borders                                                                    */
/* -------------------------------------------------------------------------- */

export const HOPE_BORDER =
  "light-dark(rgba(16,44,36,.12), rgba(220,234,223,.12))";

export const HOPE_BORDER_STRONG =
  "light-dark(rgba(16,44,36,.22), rgba(220,234,223,.22))";

/* -------------------------------------------------------------------------- */
/* Typography                                                                 */
/* -------------------------------------------------------------------------- */

export const HOPE_SERIF =
  '"Playfair Display", Georgia, "Times New Roman", serif';

export const HOPE_SANS =
  "var(--primary-font-family)";

/** Handwritten voice — margin notes, taglines, and quotes on Impact Cards. */
export const HOPE_SCRIPT =
  '"Caveat", "Segoe Script", "Bradley Hand", cursive';

/* -------------------------------------------------------------------------- */
/* Theme-aware alpha                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Theme-aware alpha helper.
 *
 * Replaces legacy `${color}88` style hex suffixes,
 * and works with CSS variables / light-dark().
 */
export function hopeWithAlpha(
  color: string,
  hexAlpha: string
): string {
  const pct = Math.round(
    (Number.parseInt(hexAlpha, 16) / 255) * 100
  );

  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
}

/* -------------------------------------------------------------------------- */
/* Brand gradient text                                                        */
/* -------------------------------------------------------------------------- */

/**
 * thehope.one gradient text
 *
 * Replaces the old metallic / gold foil language.
 *
 * This is intentionally natural and restrained:
 * forest -> green -> sage -> muted blue.
 */
export const hopeImpactGradientText = {
  color: "var(--text)",
  WebkitTextFillColor: "unset",
  background: "none",
} as const;

/* -------------------------------------------------------------------------- */
/* Optional simpler headline gradient                                         */
/* -------------------------------------------------------------------------- */

export const hopeHeadlineGradient = {
  color: "var(--text)",
  WebkitTextFillColor: "unset",
  background: "none",
} as const;

/* -------------------------------------------------------------------------- */
/* Primary button                                                             */
/* -------------------------------------------------------------------------- */

export const hopePrimaryButtonSx = {
  background:
    "light-dark(" +
    "linear-gradient(135deg, #102C24 0%, #1D4437 52%, #376B55 100%)," +
    "linear-gradient(135deg, #DCEADF 0%, #A8C9B2 52%, #8FB49B 100%)" +
    ")",

  color:
    "light-dark(#FFFFFF, #102C24)",

  fontWeight: 700,

  fontFamily: HOPE_SANS,

  textTransform: "none" as const,

  borderRadius: "999px",

  boxShadow:
    "light-dark(" +
    "0 8px 22px rgba(16,44,36,.16)," +
    "0 8px 22px rgba(0,0,0,.24)" +
    ")",

  transition:
    "transform .2s ease, box-shadow .2s ease, background .2s ease",

  "&:hover": {
    background:
      "light-dark(" +
      "linear-gradient(135deg, #17382E 0%, #295846 52%, #4F8066 100%)," +
      "linear-gradient(135deg, #EEF5EF 0%, #B8D5C0 52%, #9FC1A8 100%)" +
      ")",

    boxShadow:
      "light-dark(" +
      "0 12px 30px rgba(16,44,36,.20)," +
      "0 12px 30px rgba(0,0,0,.30)" +
      ")",

    transform: "translateY(-1px)",
  },

  "&:disabled": {
    opacity: 0.5,

    color:
      "light-dark(rgba(255,255,255,.82), rgba(16,44,36,.55))",
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Secondary button                                                           */
/* -------------------------------------------------------------------------- */

export const hopeSecondaryButtonSx = {
  bgcolor:
    "light-dark(#EEF5EF, #193129)",

  color:
    "light-dark(#102C24, #DCEADF)",

  border:
    "1px solid light-dark(rgba(16,44,36,.14), rgba(220,234,223,.14))",

  borderRadius: "999px",

  fontWeight: 700,

  fontFamily: HOPE_SANS,

  textTransform: "none" as const,

  boxShadow: "none",

  "&:hover": {
    bgcolor:
      "light-dark(#DCEADF, #203D33)",

    borderColor:
      "light-dark(#5E8F72, #8FB49B)",

    boxShadow: "none",
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Verified / positive state                                                  */
/* -------------------------------------------------------------------------- */

export const hopeVerifiedSx = {
  bgcolor:
    "light-dark(#E3F0E6, rgba(94,143,114,.16))",

  color:
    "light-dark(#4F8767, #9FC1A8)",

  border:
    "1px solid light-dark(rgba(79,135,103,.15), rgba(159,193,168,.16))",

  borderRadius: "999px",

  fontWeight: 700,
} as const;

/* -------------------------------------------------------------------------- */
/* Soft impact panel                                                          */
/* -------------------------------------------------------------------------- */

export const hopeImpactPanelSx = {
  bgcolor:
    "light-dark(#EEF5EF, #162C24)",

  color: HOPE_TEXT,

  border: `1px solid ${HOPE_BORDER}`,

  borderRadius: 3,

  boxShadow:
    "light-dark(0 10px 30px rgba(16,44,36,.06), none)",
} as const;